import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useToast } from 'use-toast-mui';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { registerGuardian, getGuardianList, updateGuardian, deleteGuardian } from '../../../api/user';
import { errorMessages } from '../../../utils/methods';

const initialGuardianData = {
  guardianName: '',
  guardianPhone: '',
  relationship: '',
  notifyOnCheckin: 'Y',
  notifyMethod: 'KAKAO',
  memo: '',
};

const GuardianRegisterModal = ({ open, onClose, memberId, onSuccess }) => {
  const toast = useToast();
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const [guardianData, setGuardianData] = useState(initialGuardianData);
  const [isLoading, setIsLoading] = useState(false);
  const [guardianList, setGuardianList] = useState([]);
  const [isListLoading, setIsListLoading] = useState(false);
  const [editingGuardianId, setEditingGuardianId] = useState(null);

  // 보호자 목록 조회
  const fetchGuardianList = async () => {
    if (!memberId || !user?.token) return;
    setIsListLoading(true);
    try {
      const response = await getGuardianList(memberId, user.token);
      setGuardianList(response || []);
    } catch (error) {
      console.error('보호자 목록 조회 실패:', error);
      setGuardianList([]);
    } finally {
      setIsListLoading(false);
    }
  };

  // 모달이 열릴 때 목록 조회
  useEffect(() => {
    if (open) {
      fetchGuardianList();
    }
  }, [open, memberId]);

  const handleClose = () => {
    setGuardianData(initialGuardianData);
    setEditingGuardianId(null);
    onClose();
  };

  // 수정 버튼 클릭
  const handleEdit = guardian => {
    setEditingGuardianId(guardian.id);
    setGuardianData({
      guardianName: guardian.guardianName || '',
      guardianPhone: guardian.guardianPhone || '',
      relationship: guardian.relationship || '',
      notifyOnCheckin: guardian.notifyOnCheckin || 'Y',
      notifyMethod: guardian.notifyMethod || 'SMS',
      memo: guardian.memo || '',
    });
  };

  // 수정 취소
  const handleCancelEdit = () => {
    setEditingGuardianId(null);
    setGuardianData(initialGuardianData);
  };

  // 삭제
  const handleDelete = async guardianId => {
    if (!window.confirm(t('정말 삭제하시겠습니까?'))) return;

    try {
      await deleteGuardian(memberId, guardianId, user.token);
      toast.success(t('보호자가 삭제되었습니다'));
      fetchGuardianList();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const handleSubmit = async () => {
    // 유효성 검사
    if (!guardianData.guardianName) {
      toast.error(t('보호자 이름을 입력해주세요'));
      return;
    }
    if (!guardianData.guardianPhone) {
      toast.error(t('보호자 연락처를 입력해주세요'));
      return;
    }
    if (!guardianData.relationship) {
      toast.error(t('관계를 입력해주세요'));
      return;
    }

    setIsLoading(true);
    try {
      if (editingGuardianId) {
        // 수정
        await updateGuardian(memberId, editingGuardianId, guardianData, user.token);
        toast.success(t('보호자가 수정되었습니다'));
        setEditingGuardianId(null);
      } else {
        // 등록
        await registerGuardian(memberId, guardianData, user.token);
        toast.success(t('보호자가 등록되었습니다'));
      }
      setGuardianData(initialGuardianData);
      fetchGuardianList(); // 목록 새로고침
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          maxHeight: '90vh',
          bgcolor: '#fff',
          borderRadius: '12px',
          boxShadow: 24,
          p: 3,
          overflow: 'auto',
        }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>{t('보호자 관리')}</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* 보호자 목록 */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 600, mb: 1.5 }}>{t('등록된 보호자')}</Typography>
          <TableContainer component={Paper} sx={{ border: '1px solid #eee' }} elevation={0}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#FAFBFC' }}>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: '13px' }}>
                    {t('이름')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: '13px' }}>
                    {t('연락처')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: '13px' }}>
                    {t('관계')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: '13px' }}>
                    {t('체크인 알림')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: '13px', width: '100px' }}>
                    {t('관리')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isListLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography sx={{ fontSize: '14px', color: '#999' }}>{t('로딩 중...')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : guardianList.length > 0 ? (
                  guardianList.map((guardian, index) => (
                    <TableRow key={guardian.id || index}>
                      <TableCell align="center" sx={{ fontSize: '13px' }}>
                        {guardian.guardianName}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '13px' }}>
                        {guardian.guardianPhone}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '13px' }}>
                        {guardian.relationship}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '13px' }}>
                        {guardian.notifyOnCheckin === 'Y' ? t('알림') : t('알림 안함')}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '13px' }}>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Button
                            size="small"
                            onClick={() => handleEdit(guardian)}
                            sx={{
                              minWidth: '40px',
                              fontSize: '12px',
                              color: '#0F7F85',
                              '&:hover': { bgcolor: '#f0f9f9' },
                            }}>
                            {t('수정')}
                          </Button>
                          <Button
                            size="small"
                            onClick={() => handleDelete(guardian.id)}
                            sx={{
                              minWidth: '40px',
                              fontSize: '12px',
                              color: '#dc2626',
                              '&:hover': { bgcolor: '#fef2f2' },
                            }}>
                            {t('삭제')}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography sx={{ fontSize: '14px', color: '#999' }}>{t('등록된 보호자가 없습니다')}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* 보호자 등록/수정 폼 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
            {editingGuardianId ? t('보호자 수정') : t('보호자 등록')}
          </Typography>
          {editingGuardianId && (
            <Button size="small" onClick={handleCancelEdit} sx={{ fontSize: '13px', color: '#666' }}>
              {t('취소')}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* 보호자 이름 */}
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('보호자 이름')} <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t('보호자 이름을 입력해주세요')}
              value={guardianData.guardianName}
              onChange={e => setGuardianData({ ...guardianData, guardianName: e.target.value })}
            />
          </Box>

          {/* 보호자 연락처 */}
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('보호자 연락처')} <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="010-0000-0000"
              value={guardianData.guardianPhone}
              onChange={e => {
                // 숫자만 추출
                const digits = e.target.value.replace(/[^0-9]/g, '');
                // 최대 11자리
                const limited = digits.slice(0, 11);
                // 하이픈 포맷팅
                let formatted = '';
                if (limited.length <= 3) {
                  formatted = limited;
                } else if (limited.length <= 7) {
                  formatted = `${limited.slice(0, 3)}-${limited.slice(3)}`;
                } else {
                  formatted = `${limited.slice(0, 3)}-${limited.slice(3, 7)}-${limited.slice(7)}`;
                }
                setGuardianData({ ...guardianData, guardianPhone: formatted });
              }}
            />
          </Box>

          {/* 관계 */}
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('관계')} <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t('관계를 입력해주세요 (예: 부모, 배우자 등)')}
              value={guardianData.relationship}
              onChange={e => setGuardianData({ ...guardianData, relationship: e.target.value })}
            />
          </Box>

          {/* 체크인 시 알림 */}
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>{t('체크인 시 알림')}</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant={guardianData.notifyOnCheckin === 'Y' ? 'contained' : 'outlined'}
                onClick={() => setGuardianData({ ...guardianData, notifyOnCheckin: 'Y' })}
                sx={{
                  flex: 1,
                  bgcolor: guardianData.notifyOnCheckin === 'Y' ? '#0F7F85' : 'transparent',
                  color: guardianData.notifyOnCheckin === 'Y' ? '#fff' : '#666',
                  borderColor: '#ddd',
                  '&:hover': {
                    bgcolor: guardianData.notifyOnCheckin === 'Y' ? '#0a5a5e' : '#f5f5f5',
                  },
                }}>
                {t('알림')}
              </Button>
              <Button
                variant={guardianData.notifyOnCheckin === 'N' ? 'contained' : 'outlined'}
                onClick={() => setGuardianData({ ...guardianData, notifyOnCheckin: 'N' })}
                sx={{
                  flex: 1,
                  bgcolor: guardianData.notifyOnCheckin === 'N' ? '#0F7F85' : 'transparent',
                  color: guardianData.notifyOnCheckin === 'N' ? '#fff' : '#666',
                  borderColor: '#ddd',
                  '&:hover': {
                    bgcolor: guardianData.notifyOnCheckin === 'N' ? '#0a5a5e' : '#f5f5f5',
                  },
                }}>
                {t('알림 안함')}
              </Button>
            </Box>
          </Box>

          {/* 메모 */}
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>{t('메모')}</Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              placeholder={t('메모를 입력해주세요')}
              value={guardianData.memo}
              onChange={e => setGuardianData({ ...guardianData, memo: e.target.value })}
            />
          </Box>
        </Box>

        {/* 버튼 */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
          <Button
            onClick={handleClose}
            sx={{
              borderRadius: '8px',
              px: 3,
              bgcolor: '#f7f7f7',
              color: '#9a9a9a',
              '&:hover': { bgcolor: '#ddd' },
            }}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            sx={{
              borderRadius: '8px',
              px: 3,
              bgcolor: '#0F7F85',
              color: '#fff',
              '&:hover': { bgcolor: '#0a5a5e' },
              '&:disabled': { bgcolor: '#ccc' },
            }}>
            {isLoading
              ? editingGuardianId
                ? t('수정 중...')
                : t('등록 중...')
              : editingGuardianId
                ? t('수정')
                : t('등록')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default GuardianRegisterModal;
